/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TenantService } from '@src/app/shared/tenant.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { FormErrorComponent } from '@src/app/shared/form/form-error/form-error.component';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { NgTemplateOutlet, NgOptimizedImage } from '@angular/common';
import { User } from '@src/app/model/user';
import { unknownUserRoute } from '@src/app/modules/user/join-an-omc/join-an-omc.component';
import { LoginPageNavRoute, PersonalRequestPageNavRoute } from '@src/app/routes/app-router';

@Component({
    selector: 'app-new-user-request',
    templateUrl: './new-user-request.component.html',
    styleUrls: ['./new-user-request.component.scss'],
    imports: [NgTemplateOutlet, MatCardModule, MatButtonModule, NgOptimizedImage, MatFormFieldModule,
        MatInputModule, FormsModule, ReactiveFormsModule, FormErrorComponent]
})
export class NewUserRequestComponent {
    private router = inject(Router);
    private http = inject(HttpClient);
    private cds = inject(ConfirmDialogService);

    emailCtl = new UntypedFormControl('');

    whoami: 'noidea'|'newuser'|'passwordNeeded'|'existing'|'requestedPassword' = 'noidea';
    requesting = false;

    constructor() {
        const currentUser = inject(CurrentUserService);

        currentUser.getCurrentUser().pipe(takeUntilDestroyed()).subscribe ( (user) => {
            if (user !== null) {
                if (user.currentTeam) {
                    if (User.canPostOwnSR(user)) {
                        this.router.navigate([PersonalRequestPageNavRoute.getListRouteName()]);
                    } else {
                        this.router.navigate(['/']);
                    }

                } else {
                    this.router.navigate([unknownUserRoute]);
                }

            }
        })
    }

    logMeIn() {
        const path = [LoginPageNavRoute.openRoute.path];
        const parms = {queryParams: {returnUrl: unknownUserRoute}};
        console.log({path, parms});
        this.router.navigate( path, parms);
    }

    createNew() {
        const title = $localize `Have you received a mail from your OMC?`;
        const msg = $localize `Have you received an email from your OMC telling you about OurOMC?`
        const choice = {
            title,
            msg,
            options: [
                { name: 'Yes', action: () => {
                    const title2 = 'You already have a user account setup'
                    const msg2 = `
                    If you received a mail from your omc @ouromc.com then you already have an account.
                    You can login with the email address that the email was sent to, there is a simple reset password option if you need a password.
                    If you want to use a different email address to login going forward, you need to request a change of email address.
                    You can do that, by logging in with the email address that you where previously contacted on, and creating a request to update owner details.
                    Alternately, you could just reply on the mail you received before, copying your preferred email address, 
                    and request the email address change that way.
                    Please do not create a new user account as it will cause confusion.
                `
                this.cds.alert(title2, msg2)
                }},
                { name: 'No', action: () => {
                    this.whoami = 'newuser';                    
                }}
            ]
        }        
        this.cds.openChoice(choice,false);
        
    }

    passwordNeeded() {
        this.whoami = 'passwordNeeded';
    }

    requestPassword() {
        const httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json;charset=UTF-8')
        };
        this.requesting = true;

        this.http.post<Response>(TenantService.bareAjaxPath + 'reset/please',
            { email: this.emailCtl.value }, httpOptions
        ).subscribe(
            () => {
                this.whoami = 'requestedPassword';
            },
            loginError => {
                console.log('loginFailed', loginError);
                this.requesting = false;
            }
        );
    }

    knownUser() {
        return this.emailCtl.value === '';
    }

    oAuthLogin(provider: string) {
        CurrentUserService.returnMeTo(PersonalRequestPageNavRoute.listRoute.path);
        window.location.href = `/api/oauth/redirect/${provider}`;
    }

}
